import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "../api";

// GSM Terminal -- a shared, no-personal-login kiosk. A player types their
// surname, the system matches them against the real roster (GET
// /api/terminal/:slug/search), and from there they can register themselves
// as playing in an upcoming competition or enter their own hole-by-hole
// score for one they're already registered in. Every write is re-validated
// server-side against this exact player/event pairing -- see terminal.js --
// since a surname match is the only "authentication" this kiosk has.
//
// Score entry (2026-10-18 rewrite): ONE Front 9 / Back 9 grid for every
// competition format, matching the desktop GSM Terminal kiosk exactly.
// Individual/shared-ball formats work as before. Team formats (Better
// Ball, Best X of Y, Team Aggregate etc.) add a player-selector row above
// the same grid -- whichever teammate is highlighted is who the grid is
// currently entering for -- replacing the old separate hole-by-hole/numpad
// team screen. A live running team total stays visible underneath
// regardless of which teammate is selected. Finish now opens a review
// screen showing every entered score before anything is actually
// submitted (external design feedback, reviewed and agreed with Mike).
export default function Terminal() {
  const { slug } = useParams();

  // Whether a real competition is currently published for this society --
  // found 2026-10-20 (Mike, live): showing the competition surname search
  // when there's nothing to register for dead-ends at an empty "which
  // competition?" screen, which reads as broken. The home screen must
  // never offer a path with nothing at the end of it.
  const [hasActiveComp, setHasActiveComp] = useState(true); // true until checked, so nothing flashes/hides prematurely

  const checkActiveCompetition = () => {
    api.get(`/terminal/${slug}/events`)
      .then(res => setHasActiveComp(Array.isArray(res.data) && res.data.length > 0))
      .catch(() => setHasActiveComp(false));
  };

  // Checked once on load (and again on gpLoadInfo's own retry/refresh
  // paths below), to decide whether to show General Play and whether the
  // competition search has anything to lead to -- the actual kiosk flow
  // re-checks everything server-side on every call regardless of these.
  useEffect(() => {
    gpLoadInfo();
    checkActiveCompetition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const [surname, setSurname] = useState("");
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [player, setPlayer] = useState(null);

  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  const [scoreEvent, setScoreEvent] = useState(null);
  const [holes, setHoles] = useState([]);
  const [partnerName, setPartnerName] = useState(null);
  const [loadingCard, setLoadingCard] = useState(false);
  const [saving, setSaving] = useState(false);
  // Playing Handicap + format (Stableford points vs Medal net) for the
  // single-player scorecard -- returned by GET /scorecard so the kiosk can
  // show live points/net per hole and Front 9/Back 9 totals the same way
  // the rest of GSM scores a round, not just raw strokes.
  const [hcpAdj, setHcpAdj] = useState(0);
  const [isMedal, setIsMedal] = useState(false);

  const [busyEventId, setBusyEventId] = useState(null);
  const [error, setError] = useState("");

  // Team formats: every team member's own gross strokes, entered via the
  // same grid, by whichever teammate is holding the kiosk. combine/isMedal/
  // teamName all come back from the server per-event (TEAM_FORMAT_CONFIG in
  // terminal.js) so this never has to guess the rule for a given format.
  const TEAM_FORMATS = [
    "Team Stableford (Best 2 of 4)", "Better Ball Stableford", "Better Ball Medal", "Better Ball Stroke Play",
    "Fourball Better Ball Stableford", "Fourball Stroke Play", "Team Stableford", "Team Medal",
    "Team Aggregate Stableford", "Team Aggregate Stroke Play",
  ];
  const [teamEvent, setTeamEvent] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamName, setTeamName] = useState(null);
  const [teamCombine, setTeamCombine] = useState({ rule: "best", n: 1 });
  const [teamIsMedal, setTeamIsMedal] = useState(false);
  const [teamHoles, setTeamHoles] = useState([]);
  const [teamScores, setTeamScores] = useState([]); // [{player_id, hole, gross}]
  const [loadingTeamCard, setLoadingTeamCard] = useState(false);
  const [savingTeam, setSavingTeam] = useState(false);
  const [activePlayerId, setActivePlayerId] = useState(null);

  // Match Play (added 2026-10-18, found via a format-classification audit
  // -- these 5 formats had no kiosk self-entry path at all before this and
  // fell through to the plain individual flow, which is wrong for a format
  // that's scored by comparing two SIDES per hole, not one player at a
  // time). Mirrors MatchPlayScoreEntry.jsx's own allowance/net formulas
  // exactly so the kiosk can never disagree with the organiser's desktop
  // screen about who's winning.
  const MATCH_PLAY_FORMATS = [
    "Better Ball Match Play", "Fourball Match Play", "Foursomes Match Play", "Greensomes Match Play", "Team Match Play",
  ];
  const [matchEvent, setMatchEvent] = useState(null);
  const [matchType, setMatchType] = useState("singles"); // fourball | alternate | singles
  const [sideA, setSideA] = useState({ name: "", members: [] });
  const [sideB, setSideB] = useState({ name: "", members: [] });
  const [matchHoles, setMatchHoles] = useState([]);
  const [matchScores, setMatchScores] = useState([]); // [{player_id, hole, gross}]
  // Who can be selected to enter a gross for -- every real player for
  // fourball/singles, but only ONE pseudo-entry per side for alternate-shot
  // (shared ball, credited to that side's lead/members[0] player, same
  // convention as TeamSingleBallScoreEntry.jsx's shared-ball screens).
  const [matchEntrants, setMatchEntrants] = useState([]);
  const [activeEntrant, setActiveEntrant] = useState(null);
  const [loadingMatchCard, setLoadingMatchCard] = useState(false);
  const [savingMatch, setSavingMatch] = useState(false);

  // Review-before-Finish (external design feedback, 2026-10-18): shows every
  // entered score -- the single player/pair's card, or every teammate's own
  // card plus the combined team result -- before anything is submitted.
  const [reviewing, setReviewing] = useState(false);
  const [finishedLabel, setFinishedLabel] = useState(null);

  const search = async (q) => {
    setSurname(q);
    if (q.trim().length < 2) { setMatches([]); return; }
    setSearching(true);
    try {
      const res = await api.get(`/terminal/${slug}/search`, { params: { q } });
      setMatches(res.data);
    } catch (e) { setMatches([]); }
    finally { setSearching(false); }
  };

  const loadEvents = async (p) => {
    setPlayer(p); setMatches([]); setError("");
    setLoadingEvents(true);
    try {
      // A 'list'-sourced match isn't a real player yet (no player_id to ask
      // already_registered about) -- promotion happens on first register.
      const params = p.source === 'list' ? {} : { player_id: p.id };
      const res = await api.get(`/terminal/${slug}/events`, { params });
      setEvents(res.data);
    } catch (e) { setError("Couldn't load competitions — please try again."); }
    finally { setLoadingEvents(false); }
  };

  const register = async (eventId) => {
    setBusyEventId(eventId); setError("");
    try {
      // Casual/drop-in play: if this match came from the society's Player
      // List fallback (not yet in the live roster), the server promotes
      // them into Players on this call and hands back the real player_id --
      // swap it into state so every later screen (scorecard, score save)
      // uses a real id from here on.
      const body = player.source === 'list'
        ? { player_list_entry_id: player.id, event_id: eventId }
        : { player_id: player.id, event_id: eventId };
      const res = await api.post(`/terminal/${slug}/register`, body);
      if (res.data.player_id) setPlayer(prev => ({ ...prev, id: res.data.player_id, source: 'player' }));
      setEvents(prev => prev.map(ev => ev.id === eventId ? { ...ev, already_registered: true } : ev));
    } catch (e) { setError(e.response?.data?.error || "Registration failed — please try again."); }
    finally { setBusyEventId(null); }
  };

  const openScorecard = async (ev) => {
    setScoreEvent(ev); setReviewing(false); setError("");
    setLoadingCard(true);
    try {
      const res = await api.get(`/terminal/${slug}/scorecard`, { params: { player_id: player.id, event_id: ev.id } });
      setHoles(res.data.holes);
      setPartnerName(res.data.partner_name || null);
      setHcpAdj(res.data.hcp_adj || 0);
      setIsMedal(!!res.data.is_medal);
    } catch (e) { setError("Couldn't load your scorecard — please try again."); setScoreEvent(null); }
    finally { setLoadingCard(false); }
  };

  const openTeamScorecard = async (ev) => {
    setTeamEvent(ev); setError(""); setReviewing(false); setActivePlayerId(null);
    setLoadingTeamCard(true);
    try {
      const res = await api.get(`/terminal/${slug}/team-strokes`, { params: { player_id: player.id, event_id: ev.id } });
      setTeamMembers(res.data.team_members);
      setTeamName(res.data.team_name || null);
      setTeamCombine(res.data.combine || { rule: "best", n: 1 });
      setTeamIsMedal(!!res.data.is_medal);
      setTeamHoles(res.data.holes);
      setTeamScores(res.data.scores);
      setActivePlayerId(res.data.team_members[0]?.id ?? null);
    } catch (e) { setError("Couldn't load your team's scorecard — please try again."); setTeamEvent(null); }
    finally { setLoadingTeamCard(false); }
  };

  const openMatchScorecard = async (ev) => {
    setMatchEvent(ev); setError(""); setReviewing(false); setActiveEntrant(null);
    setLoadingMatchCard(true);
    try {
      const res = await api.get(`/terminal/${slug}/match`, { params: { player_id: player.id, event_id: ev.id } });
      setMatchType(res.data.match_type);
      setSideA(res.data.side_a);
      setSideB(res.data.side_b);
      setMatchHoles(res.data.holes);
      setMatchScores(res.data.scores);
      const entrants = res.data.match_type === "alternate"
        ? [
            { id: res.data.side_a.members[0]?.id, name: res.data.side_a.name },
            { id: res.data.side_b.members[0]?.id, name: res.data.side_b.name },
          ]
        : [...res.data.side_a.members, ...res.data.side_b.members];
      setMatchEntrants(entrants);
      setActiveEntrant(entrants[0] || null);
    } catch (e) { setError(e.response?.data?.error || "Couldn't load this match — please try again."); setMatchEvent(null); }
    finally { setLoadingMatchCard(false); }
  };

  // ---- Match Play scoring -- mirrors MatchPlayScoreEntry.jsx's own
  // allowance/net/result formulas exactly (R&A Rules of Handicapping): the
  // LOWEST allowance anywhere in the match plays off scratch, everyone else
  // gets the difference. ----
  const matchScoreFor = (playerId, hole) => matchScores.find(s => s.player_id === playerId && s.hole === hole);
  const setMatchStroke = (playerId, hole, gross) => {
    setMatchScores(prev => {
      const idx = prev.findIndex(s => s.player_id === playerId && s.hole === hole);
      if (gross === null) { if (idx === -1) return prev; return prev.filter((_, i) => i !== idx); }
      if (idx === -1) return [...prev, { player_id: playerId, hole, gross }];
      const next = [...prev]; next[idx] = { ...next[idx], gross }; return next;
    });
  };
  const sideAllowance = (side) => {
    if (matchType === "alternate") {
      const combined = side.members.reduce((s, p) => s + (p.hcp_adj || 0), 0);
      if (matchEvent?.game_type === "Greensomes Match Play") {
        const lower = Math.min(...side.members.map(p => p.hcp_adj || 0));
        const higher = Math.max(...side.members.map(p => p.hcp_adj || 0));
        return Math.round(0.6 * lower + 0.4 * higher);
      }
      return Math.round(combined * 0.5); // Foursomes Match Play
    }
    return side.members[0]?.hcp_adj || 0; // singles -- used per-player below for fourball
  };
  const playerAllowance = (member) => {
    const raw = member?.hcp_adj || 0;
    return matchType === "fourball" ? Math.round(raw * 0.9) : raw; // singles = 100%
  };
  const getLowBaseline = () => {
    if (matchType === "alternate") return Math.min(sideAllowance(sideA), sideAllowance(sideB));
    if (matchType === "fourball") return Math.min(...[...sideA.members, ...sideB.members].map(playerAllowance));
    return Math.min(playerAllowance(sideA.members[0]), playerAllowance(sideB.members[0]));
  };
  const strokesForAllowance = (allowance, hole, lowBaseline) => {
    const diff = Math.max(0, allowance - lowBaseline);
    const base = Math.floor(diff / 18);
    const extra = diff % 18;
    return base + (hole.stroke_index <= extra ? 1 : 0);
  };
  const getSideNetForHole = (side, hole, lowBaseline) => {
    if (matchType === "alternate") {
      const gross = matchScoreFor(side.members[0]?.id, hole.hole)?.gross;
      if (!Number.isInteger(gross)) return null;
      return gross - strokesForAllowance(sideAllowance(side), hole, lowBaseline);
    }
    if (matchType === "fourball") {
      const nets = side.members.map(m => {
        const gross = matchScoreFor(m.id, hole.hole)?.gross;
        if (!Number.isInteger(gross)) return null;
        return gross - strokesForAllowance(playerAllowance(m), hole, lowBaseline);
      }).filter(n => n !== null);
      return nets.length ? Math.min(...nets) : null; // better ball -- lowest net counts
    }
    const gross = matchScoreFor(side.members[0]?.id, hole.hole)?.gross;
    if (!Number.isInteger(gross)) return null;
    return gross - strokesForAllowance(playerAllowance(side.members[0]), hole, lowBaseline);
  };
  const getHoleResult = (hole) => {
    const lowBaseline = getLowBaseline();
    const netA = getSideNetForHole(sideA, hole, lowBaseline);
    const netB = getSideNetForHole(sideB, hole, lowBaseline);
    if (netA === null || netB === null) return null;
    if (netA < netB) return "A";
    if (netB < netA) return "B";
    return "H";
  };
  const matchHoleResults = matchHoles.map(getHoleResult);
  const getMatchClosedAt = () => {
    for (let i = 0; i < matchHoles.length; i++) {
      let score = 0;
      matchHoleResults.slice(0, i + 1).forEach(r => { if (r === "A") score++; else if (r === "B") score--; });
      if (Math.abs(score) > matchHoles.length - (i + 1)) return i + 1;
    }
    return null;
  };
  const matchClosedAtHole = getMatchClosedAt();
  const getMatchStatusThrough = (upToIndex) => {
    let score = 0;
    matchHoleResults.slice(0, upToIndex + 1).forEach(r => { if (r === "A") score++; else if (r === "B") score--; });
    const holesLeft = matchHoles.length - (upToIndex + 1);
    if (score === 0) return "All Square";
    const lead = Math.abs(score);
    const leader = score > 0 ? sideA.name : sideB.name;
    if (lead > holesLeft) return `${leader} wins ${lead}&${holesLeft}`;
    return `${leader} ${lead}UP`;
  };
  const latestPlayedIndex = (() => {
    let last = -1;
    matchHoleResults.forEach((r, i) => { if (r !== null) last = i; });
    return last;
  })();
  const matchStatusNow = latestPlayedIndex === -1 ? "Match not started" : getMatchStatusThrough(latestPlayedIndex);

  const saveMatchScores = async () => {
    setSavingMatch(true); setError("");
    try {
      await api.post(`/terminal/${slug}/match/save`, { player_id: player.id, event_id: matchEvent.id, scores: matchScores });
      return true;
    } catch (e) { setError(e.response?.data?.error || "Couldn't save — please try again."); return false; }
    finally { setSavingMatch(false); }
  };

  // Same formula used everywhere else in GSM (BetterBallScoreEntry,
  // Leaderboard, GroupBestTwoResults): Stableford points = max(0, 2 + (par
  // - net)); Medal/Stroke Play formats use the raw net instead.
  const scoringValue = (gross, hcpAdj, hole) => {
    if (!Number.isInteger(gross) || !hole) return null;
    const hcp = hcpAdj || 0;
    const base = Math.floor(hcp / 18);
    const extra = hcp % 18;
    const shots = base + (hole.stroke_index <= extra ? 1 : 0);
    const net = gross - shots;
    return teamIsMedal ? net : Math.max(0, 2 + (hole.par - net));
  };
  // Sorts a list of per-member values so index 0 is always "best" for the
  // active format -- highest points for Stableford, lowest net for Medal.
  const sortBest = (values) => [...values].sort((a, b) => teamIsMedal ? a - b : b - a);

  const scoreFor = (playerId, hole) => teamScores.find(s => s.player_id === playerId && s.hole === hole);

  const setStroke = (playerId, hole, gross) => {
    setTeamScores(prev => {
      const idx = prev.findIndex(s => s.player_id === playerId && s.hole === hole);
      if (gross === null) {
        if (idx === -1) return prev;
        return prev.filter((_, i) => i !== idx);
      }
      if (idx === -1) return [...prev, { player_id: playerId, hole, gross }];
      const next = [...prev];
      next[idx] = { ...next[idx], gross };
      return next;
    });
  };

  const teamScoreForHole = (hole) => {
    const holeData = teamHoles.find(h => h.hole === hole);
    const values = teamMembers
      .map(m => scoringValue(scoreFor(m.id, hole)?.gross, m.hcp_adj, holeData))
      .filter(v => v !== null);
    if (!values.length) return null;
    if (teamCombine.rule === "sum") return values.reduce((s, v) => s + v, 0);
    return sortBest(values).slice(0, teamCombine.n || 1).reduce((s, v) => s + v, 0);
  };
  const holesCompleted = teamHoles.filter(h => teamScoreForHole(h.hole) !== null).length;
  const teamTotal = teamHoles.reduce((sum, h) => { const v = teamScoreForHole(h.hole); return sum + (v || 0); }, 0);
  const teamScoreLabel = () => {
    const n = teamMembers.length;
    if (teamCombine.rule === "sum") return "Team Score (Total)";
    if (teamCombine.n > 1) return `Team Score (Best ${teamCombine.n} of ${n})`;
    return n === 2 ? "Pair Score (Better Ball)" : "Team Score (Best 1)";
  };

  const saveTeamScores = async () => {
    setSavingTeam(true); setError("");
    try {
      await api.post(`/terminal/${slug}/team-strokes/save`, { player_id: player.id, event_id: teamEvent.id, scores: teamScores });
      return true;
    } catch (e) { setError(e.response?.data?.error || "Couldn't save — please try again."); return false; }
    finally { setSavingTeam(false); }
  };

  const resetToStart = () => {
    setFinishedLabel(null); setReviewing(false);
    setScoreEvent(null); setHoles([]); setPartnerName(null);
    setTeamEvent(null); setTeamMembers([]); setTeamHoles([]); setTeamScores([]); setActivePlayerId(null);
    setMatchEvent(null); setSideA({ name: "", members: [] }); setSideB({ name: "", members: [] });
    setMatchHoles([]); setMatchScores([]); setMatchEntrants([]); setActiveEntrant(null);
    setPlayer(null); setEvents([]); setSurname(""); setMatches([]); setError("");
    gpReset();
    // Re-check General Play availability and whether a competition is
    // published every time we're back at the home screen, not just once at
    // initial page load -- a kiosk tab stays open all day, so a single
    // transient network hiccup on load must never hide the link for the
    // whole session with no way to recover short of a full page reload.
    gpLoadInfo();
    checkActiveCompetition();
  };

  const setHoleScore = (hole, value) => {
    const gross = value === "" ? null : parseInt(value, 10);
    setHoles(prev => prev.map(h => h.hole === hole ? { ...h, gross } : h));
  };

  const saveScores = async () => {
    setSaving(true); setError("");
    try {
      const scores = holes
        .filter(h => !h.locked && Number.isInteger(h.gross) && h.gross >= 1 && h.gross <= 20)
        .map(h => ({ hole: h.hole, gross: h.gross }));
      await api.post(`/terminal/${slug}/scores/save`, { player_id: player.id, event_id: scoreEvent.id, scores });
      return true;
    } catch (e) { setError(e.response?.data?.error || "Couldn't save — please try again."); return false; }
    finally { setSaving(false); }
  };

  // ---- General Play -- a member-organised round outside any competition.
  // Fully separate state/screen set from everything above, same reasoning
  // as the Match Play addition: zero regression risk to already-proven
  // flows. Its own dedicated name search, NOT a re-use of the main surname
  // search above, because General Play members live in a separate Player
  // List (player_list_entries), not the live players roster -- confirmed
  // with Mike 2026-10-19 after the live roster turned out to be empty at
  // test time. No points/net calculation here -- General Play only ever
  // records gross, per the spec.
  const [gpInfo, setGpInfo] = useState(null); // { enabled, course_name }
  const [gpScreen, setGpScreen] = useState(null); // null | 'search' | 'choice' | 'preview' | 'score' | 'review' | 'done'
  const [gpSurname, setGpSurname] = useState("");
  const [gpMatches, setGpMatches] = useState([]);
  const [gpSearching, setGpSearching] = useState(false);
  const [gpSelectedEntry, setGpSelectedEntry] = useState(null); // { id, name } -- a player_list_entries row
  // true = has an active registered-but-unscored round (show Enter Score only),
  // false = no active round (show Register Round only), null = unknown/check
  // failed (show both, same as previous behaviour, so a network hiccup never
  // strands the player with no button at all).
  const [gpHasActiveRound, setGpHasActiveRound] = useState(null);
  const [gpCheckingStatus, setGpCheckingStatus] = useState(false);
  const [gpPreview, setGpPreview] = useState(null);
  const [gpRound, setGpRound] = useState(null);
  const [gpHoles, setGpHoles] = useState([]);
  const [gpBusy, setGpBusy] = useState(false);
  const [gpError, setGpError] = useState("");

  const gpReset = () => {
    setGpScreen(null); setGpSurname(""); setGpMatches([]); setGpSelectedEntry(null);
    setGpHasActiveRound(null); setGpCheckingStatus(false);
    setGpPreview(null); setGpRound(null); setGpHoles([]); setGpError("");
  };

  const gpLoadInfo = async (retried) => {
    try {
      const res = await api.get(`/terminal/${slug}/general-play/info`);
      setGpInfo(res.data);
    } catch (e) {
      setGpInfo(null);
      // One short automatic retry -- a transient hiccup right at page load
      // shouldn't hide General Play for the rest of the session; resetToStart()
      // also re-checks on every return to the home screen as a further safety net.
      if (!retried) setTimeout(() => gpLoadInfo(true), 3000);
    }
  };

  const gpOpenSearch = () => {
    gpReset();
    setGpScreen("search");
    gpLoadInfo();
  };

  // No competition published + General Play available -> land straight on
  // General Play's own search screen, which already has the identical
  // title/input layout as the competition screen (Mike, 2026-10-20:
  // General Play's screen must look like the competition screen, not a
  // separate custom prompt). Guarded by gpScreen === null so this only
  // fires once per visit to the home screen, never re-triggering mid-flow.
  useEffect(() => {
    if (gpScreen === null && !hasActiveComp && gpInfo?.enabled) gpOpenSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveComp, gpInfo, gpScreen]);

  const gpSearch = async (q) => {
    setGpSurname(q);
    if (q.trim().length < 2) { setGpMatches([]); return; }
    setGpSearching(true);
    try {
      const res = await api.get(`/terminal/${slug}/general-play/search`, { params: { q } });
      setGpMatches(res.data);
    } catch (e) { setGpMatches([]); }
    finally { setGpSearching(false); }
  };

  const gpSelectEntry = async (entry) => {
    setGpSelectedEntry(entry);
    setGpMatches([]);
    setGpError("");
    setGpScreen("choice");
    setGpHasActiveRound(null);
    setGpCheckingStatus(true);
    try {
      await api.get(`/terminal/${slug}/general-play/mine`, { params: { entry_id: entry.id } });
      setGpHasActiveRound(true);
    } catch (e) {
      // 404 = no active round yet, the normal/common case. Any other failure
      // (network blip, 500, etc) leaves gpHasActiveRound at null, which falls
      // back to showing both buttons rather than guessing wrong.
      setGpHasActiveRound(e.response?.status === 404 ? false : null);
    } finally {
      setGpCheckingStatus(false);
    }
  };

  const gpStartRegister = async () => {
    setGpBusy(true); setGpError("");
    try {
      const res = await api.get(`/terminal/${slug}/general-play/preview`, { params: { entry_id: gpSelectedEntry.id } });
      setGpPreview(res.data);
      setGpScreen("preview");
    } catch (e) { setGpError(e.response?.data?.error || "Couldn't load your details — please try again."); }
    finally { setGpBusy(false); }
  };

  const gpConfirmRegister = async () => {
    setGpBusy(true); setGpError("");
    try {
      const res = await api.post(`/terminal/${slug}/general-play/register`, { entry_id: gpSelectedEntry.id });
      setGpRound(res.data);
      setGpScreen("registered");
    } catch (e) { setGpError(e.response?.data?.error || "Registration failed — please try again."); }
    finally { setGpBusy(false); }
  };

  const gpStartScore = async () => {
    setGpBusy(true); setGpError("");
    try {
      const res = await api.get(`/terminal/${slug}/general-play/mine`, { params: { entry_id: gpSelectedEntry.id } });
      setGpRound(res.data);
      setGpHoles(res.data.holes);
      setGpScreen("score");
    } catch (e) { setGpError(e.response?.data?.error || "No registered General Play round found — register a round first."); }
    finally { setGpBusy(false); }
  };

  const gpSetHole = (hole, value) => {
    const gross = value === "" ? null : parseInt(value, 10);
    setGpHoles(prev => prev.map(h => h.hole === hole ? { ...h, gross } : h));
  };

  const gpSaveAndReview = async () => {
    setGpBusy(true); setGpError("");
    try {
      const scores = gpHoles.filter(h => Number.isInteger(h.gross) && h.gross >= 1 && h.gross <= 20).map(h => ({ hole: h.hole, gross: h.gross }));
      await api.post(`/terminal/${slug}/general-play/save`, { entry_id: gpSelectedEntry.id, round_id: gpRound.round_id, scores });
      setGpScreen("review");
    } catch (e) { setGpError(e.response?.data?.error || "Couldn't save — please try again."); }
    finally { setGpBusy(false); }
  };

  const gpSubmit = async () => {
    setGpBusy(true); setGpError("");
    try {
      await api.post(`/terminal/${slug}/general-play/submit`, { entry_id: gpSelectedEntry.id, round_id: gpRound.round_id });
      setGpScreen("done");
    } catch (e) { setGpError(e.response?.data?.error || "Couldn't submit — please try again."); }
    finally { setGpBusy(false); }
  };

  const Card = ({ children, wide }) => (
    <div style={{ background: "white", borderRadius: "16px", padding: "28px 24px", maxWidth: wide ? "760px" : "420px", width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.10)" }}>{children}</div>
  );
  const Page = ({ children, wide }) => (
    <div style={{ minHeight: "100vh", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <Card wide={wide}>{children}</Card>
    </div>
  );
  const backBtn = (onClick, label) => (
    <button onClick={onClick} style={{ background: "none", border: "none", color: "#166534", fontSize: "13px", fontWeight: 600, cursor: "pointer", padding: 0, marginBottom: "16px" }}>
      ← {label}
    </button>
  );

  // Confirmation screen after Finish -- a kiosk must always return to a
  // neutral, ready-for-the-next-group state, not leave the previous
  // player's/team's identity on screen.
  if (finishedLabel) {
    return (
      <Page>
        <div style={{ fontSize: "40px", textAlign: "center", marginBottom: "8px" }}>✓</div>
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "4px", textAlign: "center" }}>Scores saved</div>
        <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "20px", textAlign: "center" }}>{finishedLabel} — thank you!</div>
        <button onClick={resetToStart}
          style={{ width: "100%", background: "#166534", color: "white", border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}>
          Start Next Group →
        </button>
      </Page>
    );
  }

  // Screen 2.5: Match Play -- structurally different from every other
  // format (a hole is won/lost/halved by comparing two SIDES' nets, not
  // scored per player in isolation), so it gets its own screen rather than
  // forcing it into the single-value Front 9/Back 9 grid above.
  if (matchEvent) {
    if (loadingMatchCard) {
      return (
        <div style={{ minHeight: "100vh", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ color: "#6b7280", fontSize: "13px" }}>Loading this match…</div>
        </div>
      );
    }

    if (reviewing) {
      return (
        <div style={{ minHeight: "100vh", background: "#f0fdf4", padding: "20px", boxSizing: "border-box" }}>
          <div style={{ maxWidth: "820px", margin: "0 auto", background: "white", borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.10)", padding: "20px", boxSizing: "border-box" }}>
            {backBtn(() => setReviewing(false), "Back to entry")}
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "4px" }}>Review Match</div>
            <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "16px" }}>Check every score before submitting.</div>
            {error && <div style={{ color: "#b91c1c", fontSize: "13px", marginBottom: "12px" }}>{error}</div>}
            <div style={{ background: "#166534", color: "white", borderRadius: "8px", padding: "12px 16px", fontWeight: 700, fontSize: "18px", marginBottom: "16px", textAlign: "center" }}>
              {matchStatusNow}
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "640px" }}>
                <thead>
                  <tr style={{ background: "#166534", color: "white" }}>
                    <th style={{ padding: "8px", textAlign: "left" }}>Hole</th>
                    {matchHoles.map(h => <th key={h.hole} style={{ padding: "6px", textAlign: "center", fontSize: "16px" }}>{h.hole}</th>)}
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                    <td style={{ padding: "8px", fontWeight: 700, fontSize: "16px", color: "#6b7280" }}>Par</td>
                    {matchHoles.map(h => <td key={h.hole} style={{ padding: "6px", textAlign: "center", fontSize: "16px", color: "#9ca3af" }}>{h.par}</td>)}
                  </tr>
                  {matchEntrants.map(m => (
                    <tr key={m.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "8px", fontWeight: 600, fontSize: "18px", color: "#111827" }}>{m.name}</td>
                      {matchHoles.map(h => <td key={h.hole} style={{ padding: "6px", textAlign: "center", fontSize: "18px" }}>{matchScoreFor(m.id, h.hole)?.gross ?? "-"}</td>)}
                    </tr>
                  ))}
                  <tr style={{ background: "#f0fdf4" }}>
                    <td style={{ padding: "8px", fontWeight: 800, fontSize: "16px", color: "#14532d" }}>Result</td>
                    {matchHoles.map((h, i) => {
                      const r = matchHoleResults[i];
                      const label = matchClosedAtHole && h.hole > matchClosedAtHole ? "" : r === "A" ? "●A" : r === "B" ? "●B" : r === "H" ? "½" : "";
                      return <td key={h.hole} style={{ padding: "6px", textAlign: "center", fontWeight: 700, fontSize: "16px", color: r ? "#1d4ed8" : "#d1d5db" }}>{label}</td>;
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "10px" }}>●A = {sideA.name} won the hole · ●B = {sideB.name} won the hole · ½ = halved</div>
            <button
              onClick={async () => {
                const ok = await saveMatchScores();
                if (ok) setFinishedLabel(`${sideA.name} vs ${sideB.name}`);
              }}
              disabled={savingMatch}
              style={{ marginTop: "20px", width: "100%", background: "#166534", color: "white", border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 700, cursor: "pointer", opacity: savingMatch ? 0.6 : 1 }}>
              {savingMatch ? "Saving…" : "Confirm & Submit"}
            </button>
          </div>
        </div>
      );
    }

    return (
      <Page wide>
        {backBtn(() => setMatchEvent(null), "Back to competitions")}
        <div style={{ fontSize: "24px", fontWeight: 700, color: "#14532d", marginBottom: "2px" }}>{sideA.name} vs {sideB.name}</div>
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#2f6b40", marginBottom: "2px" }}>{matchEvent.game_type}</div>
        <div style={{ fontSize: "16px", color: "#6b7280", marginBottom: "16px" }}>{matchEvent.venue} — {new Date(matchEvent.date).toLocaleDateString("en-GB")}</div>

        {matchType === "alternate" && (
          <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8", fontSize: "16px", padding: "10px 14px", borderRadius: "8px", marginBottom: "12px" }}>
            Each side shares one ball — enter your side's single score per hole.
          </div>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "16px" }}>
          {matchEntrants.map(m => {
            const done = matchHoles.length > 0 && matchHoles.every(h => Number.isInteger(matchScoreFor(m.id, h.hole)?.gross));
            const active = m.id === activeEntrant?.id;
            return (
              <button key={m.id} onClick={() => setActiveEntrant(m)}
                style={{
                  padding: "14px 24px", borderRadius: "999px", fontSize: "20px", fontWeight: 700, cursor: "pointer",
                  border: active ? "2px solid #1d4ed8" : done ? "2px solid #86efac" : "2px solid #d1d5db",
                  background: active ? "#eff6ff" : done ? "#dcfce7" : "white",
                  color: active ? "#1d4ed8" : done ? "#166534" : "#111827",
                }}>
                {m.name}{done ? " ✓" : ""}
              </button>
            );
          })}
        </div>

        {error && <div style={{ color: "#b91c1c", fontSize: "13px", marginBottom: "12px" }}>{error}</div>}

        {(() => {
          const nine = (label, slice) => (
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "20px", fontWeight: 700, color: "#6b7280", marginBottom: "6px" }}>{label}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(9, 1fr)", gap: "6px", marginBottom: "8px" }}>
                {slice.map(h => {
                  const gross = activeEntrant ? matchScoreFor(activeEntrant.id, h.hole)?.gross ?? null : null;
                  const filled = Number.isInteger(gross);
                  const closed = matchClosedAtHole && h.hole > matchClosedAtHole;
                  return (
                    <div key={h.hole} style={{ textAlign: "center", opacity: closed ? 0.4 : 1 }}>
                      <div style={{ fontSize: "18px", color: "#9ca3af" }}>{h.hole}</div>
                      <div style={{ fontSize: "16px", color: "#9ca3af", marginBottom: "3px" }}>Par {h.par}</div>
                      <input
                        type="number" min="1" max="20"
                        value={gross ?? ""}
                        disabled={!activeEntrant || closed}
                        onChange={e => setMatchStroke(activeEntrant.id, h.hole, e.target.value === "" ? null : parseInt(e.target.value, 10))}
                        style={{
                          width: "100%", textAlign: "center", padding: "10px 2px", fontSize: "26px", fontWeight: 700,
                          border: filled ? "1.5px solid #86efac" : "1.5px solid #d1d5db", borderRadius: "8px",
                          background: filled ? "#dcfce7" : "white", color: filled ? "#166534" : "#111827",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
          const front = matchHoles.slice(0, 9);
          const back = matchHoles.slice(9, 18);
          return (
            <>
              {nine("Front 9", front)}
              {nine("Back 9", back)}
            </>
          );
        })()}

        <div style={{ background: "#166534", borderRadius: "8px", padding: "10px 14px", textAlign: "center", fontSize: "20px", fontWeight: 800, color: "white", marginBottom: "16px" }}>
          {matchStatusNow}
        </div>

        <button onClick={() => setReviewing(true)}
          style={{ width: "100%", background: "#166534", color: "white", border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}>
          Finish
        </button>
      </Page>
    );
  }

  // Screen 3: score entry -- one Front 9 / Back 9 grid for every format.
  if (scoreEvent || teamEvent) {
    const isTeam = !!teamEvent;
    const activeEvent = isTeam ? teamEvent : scoreEvent;
    const activeMember = isTeam ? teamMembers.find(m => m.id === activePlayerId) : null;
    const activeHcpAdj = isTeam ? (activeMember ? activeMember.hcp_adj : 0) : hcpAdj;
    const activeIsMedal = isTeam ? teamIsMedal : isMedal;
    const activeHoles = isTeam ? teamHoles : holes;
    const headerName = isTeam ? (teamName || (teamMembers.length ? "Team " + teamMembers[0].name.split(" ")[0] : "Your Team")) : player.name;
    const valueLabel = activeIsMedal ? "Net" : "Points";

    const grossAt = (hole) => isTeam ? (scoreFor(activePlayerId, hole)?.gross ?? null) : (holes.find(h => h.hole === hole)?.gross ?? null);
    const lockedAt = (hole) => isTeam ? false : !!(holes.find(h => h.hole === hole)?.locked);
    const pointsAt = (gross, holeData) => {
      if (!Number.isInteger(gross) || !holeData) return null;
      const hcp = activeHcpAdj || 0;
      const base = Math.floor(hcp / 18);
      const extra = hcp % 18;
      const shots = base + (holeData.stroke_index <= extra ? 1 : 0);
      const net = gross - shots;
      return activeIsMedal ? net : Math.max(0, 2 + (holeData.par - net));
    };
    const onHoleChange = (hole, value) => {
      if (isTeam) {
        if (!activePlayerId) return;
        setStroke(activePlayerId, hole, value === "" ? null : parseInt(value, 10));
      } else {
        setHoleScore(hole, value);
      }
    };

    if (loadingCard || loadingTeamCard) {
      return (
        <div style={{ minHeight: "100vh", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ color: "#6b7280", fontSize: "13px" }}>Loading your scorecard…</div>
        </div>
      );
    }

    // Review before Finish -- every entered score, checked before it's
    // actually submitted.
    if (reviewing) {
      return (
        <div style={{ minHeight: "100vh", background: "#f0fdf4", padding: "20px", boxSizing: "border-box" }}>
          <div style={{ maxWidth: "820px", margin: "0 auto", background: "white", borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.10)", padding: "20px", boxSizing: "border-box" }}>
            {backBtn(() => setReviewing(false), "Back to entry")}
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "4px" }}>Review Scorecard</div>
            <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "16px" }}>
              {isTeam ? "Check every player's scores before submitting." : "Check your scores before submitting."}
            </div>
            {error && <div style={{ color: "#b91c1c", fontSize: "13px", marginBottom: "12px" }}>{error}</div>}
            {/* Front 9 / Back 9, same split already used on the entry screen
                (2026-10-21) — a single 18-column table needed horizontal
                scrolling to see hole 18 at all on a normal kiosk width,
                which looked cut off/broken rather than scrollable. */}
            {(() => {
              const renderHalf = (label, holesSubset) => (
                <div key={label} style={{ marginBottom: "10px" }}>
                  <div style={{ fontSize: "12px", fontWeight: 700, color: "#166534", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</div>
                  <table style={{ borderCollapse: "collapse", width: "100%" }}>
                    <thead>
                      <tr style={{ background: "#166534", color: "white" }}>
                        <th style={{ padding: "6px", textAlign: "left" }}>Hole</th>
                        {holesSubset.map(h => <th key={h.hole} style={{ padding: "4px", textAlign: "center", fontSize: "14px" }}>{h.hole}</th>)}
                        <th style={{ padding: "4px", textAlign: "center", fontSize: "14px" }}>Tot</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                        <td style={{ padding: "6px", fontWeight: 700, fontSize: "14px", color: "#6b7280" }}>Par</td>
                        {holesSubset.map(h => <td key={h.hole} style={{ padding: "4px", textAlign: "center", fontSize: "14px", color: "#9ca3af" }}>{h.par}</td>)}
                        <td style={{ padding: "4px", textAlign: "center", fontSize: "14px", color: "#9ca3af" }}>{holesSubset.reduce((s, h) => s + h.par, 0)}</td>
                      </tr>
                      {isTeam ? (
                        <>
                          {teamMembers.map(m => {
                            let total = 0;
                            return (
                              <tr key={m.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                                <td style={{ padding: "6px", fontWeight: 600, fontSize: "15px", color: "#111827" }}>{m.name}</td>
                                {holesSubset.map(h => {
                                  const g = scoreFor(m.id, h.hole)?.gross;
                                  if (Number.isInteger(g)) total += g;
                                  return <td key={h.hole} style={{ padding: "4px", textAlign: "center", fontSize: "15px" }}>{g ?? "-"}</td>;
                                })}
                                <td style={{ padding: "4px", textAlign: "center", fontWeight: 700, fontSize: "15px" }}>{total}</td>
                              </tr>
                            );
                          })}
                          <tr style={{ background: "#f0fdf4" }}>
                            <td style={{ padding: "6px", fontWeight: 800, fontSize: "15px", color: "#14532d" }}>{teamScoreLabel()}</td>
                            {holesSubset.map(h => {
                              const v = teamScoreForHole(h.hole);
                              return <td key={h.hole} style={{ padding: "4px", textAlign: "center", fontWeight: 700, fontSize: "15px", color: v !== null ? "#166534" : "#d1d5db" }}>{v ?? "-"}</td>;
                            })}
                            <td style={{ padding: "4px", textAlign: "center", fontWeight: 700, fontSize: "15px", color: "#1d4ed8" }}>
                              {holesSubset.reduce((s, h) => { const v = teamScoreForHole(h.hole); return s + (v || 0); }, 0)}
                            </td>
                          </tr>
                        </>
                      ) : (
                        <tr style={{ background: "#f0fdf4" }}>
                          <td style={{ padding: "6px", fontWeight: 800, fontSize: "15px", color: "#14532d" }}>{player.name}</td>
                          {holesSubset.map(h => <td key={h.hole} style={{ padding: "4px", textAlign: "center", fontWeight: 700, fontSize: "15px" }}>{h.gross ?? "-"}</td>)}
                          <td style={{ padding: "4px", textAlign: "center", fontWeight: 700, fontSize: "15px", color: "#1d4ed8" }}>{holesSubset.reduce((s, h) => s + (h.gross || 0), 0)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
              return (
                <>
                  {renderHalf("Front 9", activeHoles.slice(0, 9))}
                  {renderHalf("Back 9", activeHoles.slice(9, 18))}
                  <div style={{ background: "#166534", borderRadius: "8px", padding: "10px 14px", textAlign: "center", fontSize: "16px", fontWeight: 800, color: "white", marginBottom: "16px" }}>
                    Total: {isTeam ? teamTotal : holes.reduce((s, h) => s + (h.gross || 0), 0)}
                  </div>
                </>
              );
            })()}
            <button
              onClick={async () => {
                const ok = isTeam ? await saveTeamScores() : await saveScores();
                if (ok) {
                  setFinishedLabel(isTeam
                    ? (teamName || (teamMembers.length ? "Team " + teamMembers[0].name.split(" ")[0] : "Your team"))
                    : player.name);
                }
              }}
              disabled={saving || savingTeam}
              style={{ marginTop: "20px", width: "100%", background: "#166534", color: "white", border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 700, cursor: "pointer", opacity: (saving || savingTeam) ? 0.6 : 1 }}>
              {(saving || savingTeam) ? "Saving…" : "Confirm & Submit"}
            </button>
          </div>
        </div>
      );
    }

    return (
      <Page wide>
        {backBtn(() => { isTeam ? setTeamEvent(null) : setScoreEvent(null); }, "Back to competitions")}
        <div style={{ fontSize: "28px", fontWeight: 700, color: "#14532d", marginBottom: "2px" }}>{headerName}</div>
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#2f6b40", marginBottom: "2px" }}>{activeEvent.game_type}</div>
        <div style={{ fontSize: "16px", color: "#6b7280", marginBottom: "16px" }}>{activeEvent.venue} — {new Date(activeEvent.date).toLocaleDateString("en-GB")}</div>

        {!isTeam && partnerName && (
          <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8", fontSize: "16px", padding: "10px 14px", borderRadius: "8px", marginBottom: "12px" }}>
            You and {partnerName} share one ball in this format — enter your pair's single score per hole.
          </div>
        )}

        {isTeam && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "16px" }}>
            {teamMembers.map(m => {
              const done = teamHoles.every(h => Number.isInteger(scoreFor(m.id, h.hole)?.gross));
              const active = m.id === activePlayerId;
              return (
                <button key={m.id} onClick={() => setActivePlayerId(m.id)}
                  style={{
                    padding: "14px 24px", borderRadius: "999px", fontSize: "20px", fontWeight: 700, cursor: "pointer",
                    border: active ? "2px solid #1d4ed8" : done ? "2px solid #86efac" : "2px solid #d1d5db",
                    background: active ? "#eff6ff" : done ? "#dcfce7" : "white",
                    color: active ? "#1d4ed8" : done ? "#166534" : "#111827",
                  }}>
                  {m.name}{done ? " ✓" : ""}
                </button>
              );
            })}
          </div>
        )}

        {error && <div style={{ color: "#b91c1c", fontSize: "13px", marginBottom: "12px" }}>{error}</div>}

        {(() => {
          const nine = (label, slice) => {
            const grosses = slice.map(h => grossAt(h.hole));
            const complete = grosses.every(g => Number.isInteger(g));
            const strokes = grosses.reduce((s, g) => s + g, 0);
            const points = slice.reduce((s, h, i) => s + (pointsAt(grosses[i], h) || 0), 0);
            const who = isTeam && activeMember ? `${activeMember.name}'s ` : "";
            return (
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "20px", fontWeight: 700, color: "#6b7280", marginBottom: "6px" }}>{label}</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(9, 1fr)", gap: "6px", marginBottom: "8px" }}>
                  {slice.map(h => {
                    const gross = grossAt(h.hole);
                    const locked = lockedAt(h.hole);
                    const filled = locked || Number.isInteger(gross);
                    return (
                      <div key={h.hole} style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "18px", color: "#9ca3af" }}>{h.hole}</div>
                        <div style={{ fontSize: "16px", color: "#9ca3af", marginBottom: "3px" }}>Par {h.par}</div>
                        <input
                          type="number" min="1" max="20"
                          value={gross ?? ""}
                          disabled={locked || (isTeam && !activePlayerId)}
                          onChange={e => onHoleChange(h.hole, e.target.value)}
                          style={{
                            width: "100%", textAlign: "center", padding: "10px 2px", fontSize: "26px", fontWeight: 700,
                            border: filled ? "1.5px solid #86efac" : "1.5px solid #d1d5db", borderRadius: "8px",
                            background: filled ? "#dcfce7" : "white", color: filled ? "#166534" : "#111827",
                            boxSizing: "border-box", transition: "background 0.15s ease, border-color 0.15s ease",
                          }}
                        />
                        {locked && <div style={{ fontSize: "16px", color: "#16a34a", marginTop: "2px" }}>✓</div>}
                      </div>
                    );
                  })}
                </div>
                {complete && (
                  <div style={{ background: "#dcfce7", border: "1px solid #86efac", borderRadius: "8px", padding: "8px 14px", display: "flex", justifyContent: "space-between", fontSize: "20px", fontWeight: 700, color: "#14532d" }}>
                    <span>{who}{label} Total</span>
                    <span>Strokes: {strokes} &nbsp;·&nbsp; {valueLabel}: {points}</span>
                  </div>
                )}
              </div>
            );
          };
          const front = activeHoles.slice(0, 9);
          const back = activeHoles.slice(9, 18);
          const roundGrosses = activeHoles.map(h => grossAt(h.hole));
          const roundComplete = activeHoles.length === 18 && roundGrosses.every(g => Number.isInteger(g));
          return (
            <>
              {nine("Front 9", front)}
              {nine("Back 9", back)}
              {roundComplete && (
                <div style={{ background: "#166534", borderRadius: "8px", padding: "10px 14px", display: "flex", justifyContent: "space-between", fontSize: "22px", fontWeight: 800, color: "white", marginBottom: "16px" }}>
                  <span>{isTeam && activeMember ? `${activeMember.name}'s ` : ""}Round Total</span>
                  <span>
                    Strokes: {roundGrosses.reduce((s, g) => s + g, 0)} &nbsp;·&nbsp;
                    {valueLabel}: {activeHoles.reduce((s, h, i) => s + (pointsAt(roundGrosses[i], h) || 0), 0)}
                  </span>
                </div>
              )}
              {isTeam && (
                <div style={{ background: "#12294b", borderRadius: "8px", padding: "10px 14px", display: "flex", justifyContent: "space-between", fontSize: "22px", fontWeight: 800, color: "white", marginBottom: "16px" }}>
                  <span>{teamScoreLabel()}</span>
                  <span>After {holesCompleted} Hole{holesCompleted !== 1 ? "s" : ""}: {teamTotal}</span>
                </div>
              )}
            </>
          );
        })()}

        <button onClick={() => setReviewing(true)}
          style={{ width: "100%", background: "#166534", color: "white", border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}>
          Finish
        </button>
      </Page>
    );
  }

  // Screen 2: events list for this player
  if (player) {
    return (
      <Page>
        {backBtn(() => setPlayer(null), "Search again")}
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "4px" }}>Hi {player.name.split(" ")[0]}</div>
        <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "16px" }}>Choose a competition below.</div>
        {error && <div style={{ color: "#b91c1c", fontSize: "13px", marginBottom: "12px" }}>{error}</div>}
        {loadingEvents ? (
          <div style={{ color: "#6b7280", fontSize: "13px", textAlign: "center", padding: "20px" }}>Loading…</div>
        ) : events.length === 0 ? (
          <div style={{ color: "#6b7280", fontSize: "13px" }}>No upcoming competitions right now.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {events.map(ev => (
              <div key={ev.id} style={{ padding: "14px", border: "1px solid #d1fae5", borderRadius: "10px" }}>
                <div style={{ fontWeight: 700, fontSize: "14px", color: "#14532d" }}>{ev.venue}</div>
                <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "10px" }}>
                  {new Date(ev.date).toLocaleDateString("en-GB")} · {ev.game_type}
                </div>
                {ev.already_registered ? (
                  <button onClick={() => MATCH_PLAY_FORMATS.includes(ev.game_type) ? openMatchScorecard(ev) : TEAM_FORMATS.includes(ev.game_type) ? openTeamScorecard(ev) : openScorecard(ev)}
                    style={{ width: "100%", background: "#1d4ed8", color: "white", border: "none", borderRadius: "8px", padding: "10px", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}>
                    Enter Score
                  </button>
                ) : (
                  <button onClick={() => register(ev.id)} disabled={busyEventId === ev.id}
                    style={{ width: "100%", background: "#166534", color: "white", border: "none", borderRadius: "8px", padding: "10px", fontSize: "13px", fontWeight: 700, cursor: "pointer", opacity: busyEventId === ev.id ? 0.6 : 1 }}>
                    {busyEventId === ev.id ? "Registering…" : "Register — I'm Playing"}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Page>
    );
  }

  // General Play screens
  if (gpScreen === "search") {
    return (
      <Page>
        {/* No competition today -- this IS the home screen, nothing to go back to. */}
        {hasActiveComp && backBtn(gpReset, "Back")}
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "4px", textAlign: "center" }}>General Play{gpInfo?.course_name ? ` — ${gpInfo.course_name}` : ""}</div>
        <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "20px", textAlign: "center" }}>Type your surname to get started</div>
        <input
          type="text" value={gpSurname} onChange={e => gpSearch(e.target.value)} placeholder="Surname"
          autoFocus
          style={{ width: "100%", border: "1.5px solid #d1d5db", borderRadius: "10px", padding: "13px 14px", fontSize: "16px", boxSizing: "border-box", marginBottom: "12px" }}
        />
        {gpSearching && <div style={{ color: "#9ca3af", fontSize: "12px" }}>Searching…</div>}
        {gpMatches.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {gpMatches.map(m => (
              <button key={m.id} onClick={() => gpSelectEntry(m)}
                style={{ textAlign: "left", background: "#f0fdf4", border: "1px solid #d1fae5", borderRadius: "8px", padding: "12px 14px", fontSize: "14px", fontWeight: 600, color: "#14532d", cursor: "pointer" }}>
                {m.name}
              </button>
            ))}
          </div>
        )}
        {gpSurname.trim().length >= 2 && !gpSearching && gpMatches.length === 0 && (
          <div style={{ color: "#9ca3af", fontSize: "12px" }}>No match found — check the spelling, or ask your organiser to add you.</div>
        )}
      </Page>
    );
  }

  if (gpScreen === "choice" && gpSelectedEntry) {
    return (
      <Page>
        {backBtn(() => { setGpScreen("search"); setGpSelectedEntry(null); setGpHasActiveRound(null); }, "Not me")}
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "16px", textAlign: "center" }}>{gpSelectedEntry.name}</div>
        {gpError && <div style={{ color: "#b91c1c", fontSize: "13px", marginBottom: "12px" }}>{gpError}</div>}
        {gpCheckingStatus ? (
          <div style={{ color: "#9ca3af", fontSize: "13px", textAlign: "center", padding: "10px" }}>Checking…</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {gpHasActiveRound !== true && (
              <button onClick={gpStartRegister} disabled={gpBusy}
                style={{ background: "#166534", color: "white", border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 700, cursor: "pointer", opacity: gpBusy ? 0.6 : 1 }}>
                Register Round
              </button>
            )}
            {gpHasActiveRound !== false && (
              <button onClick={gpStartScore} disabled={gpBusy}
                style={{ background: "#1d4ed8", color: "white", border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 700, cursor: "pointer", opacity: gpBusy ? 0.6 : 1 }}>
                Enter Score
              </button>
            )}
          </div>
        )}
      </Page>
    );
  }

  if (gpScreen === "preview" && gpPreview) {
    return (
      <Page>
        {backBtn(() => setGpScreen("choice"), "Back")}
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "16px" }}>General Play</div>
        {gpError && <div style={{ color: "#b91c1c", fontSize: "13px", marginBottom: "12px" }}>{gpError}</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "20px" }}>
          {[["Name", gpPreview.player_name], ["Club", gpPreview.society_name], ["Course", gpPreview.course_name], ["Current Handicap", gpPreview.handicap], ["Date", new Date().toLocaleDateString("en-GB")]].map(([label, value]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: "14px", padding: "8px 0", borderBottom: "1px solid #f3f4f6" }}>
              <span style={{ color: "#6b7280" }}>{label}</span><span style={{ fontWeight: 700, color: "#111827" }}>{value}</span>
            </div>
          ))}
        </div>
        <button onClick={gpConfirmRegister} disabled={gpBusy}
          style={{ width: "100%", background: "#166534", color: "white", border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 700, cursor: "pointer", opacity: gpBusy ? 0.6 : 1 }}>
          {gpBusy ? "Confirming…" : "Confirm General Play Round"}
        </button>
      </Page>
    );
  }

  if (gpScreen === "registered" && gpRound) {
    return (
      <Page>
        <div style={{ fontSize: "40px", textAlign: "center", marginBottom: "8px" }}>✓</div>
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "10px", textAlign: "center" }}>{gpRound.player_name}</div>
        <div style={{ fontSize: "14px", color: "#374151", textAlign: "center", marginBottom: "20px" }}>
          Your General Play round has been registered. Please return to this kiosk after your round to enter your score.
        </div>
        <button onClick={resetToStart}
          style={{ width: "100%", background: "#166534", color: "white", border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}>
          Done
        </button>
      </Page>
    );
  }

  if (gpScreen === "score") {
    return (
      <Page wide>
        {backBtn(resetToStart, "Back")}
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "2px" }}>{gpRound.course_name} — General Play</div>
        <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "16px" }}>{gpRound.player_name}</div>
        {gpError && <div style={{ color: "#b91c1c", fontSize: "13px", marginBottom: "12px" }}>{gpError}</div>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "8px", marginBottom: "20px" }}>
          {gpHoles.map(h => (
            <div key={h.hole} style={{ textAlign: "center" }}>
              <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "2px" }}>Hole {h.hole}</div>
              <div style={{ fontSize: "10px", color: "#9ca3af", marginBottom: "4px" }}>Par {h.par}</div>
              <input type="number" min="1" max="20" value={h.gross ?? ""} onChange={e => gpSetHole(h.hole, e.target.value)}
                style={{ width: "100%", textAlign: "center", padding: "10px 4px", fontSize: "16px", fontWeight: 700, border: "1.5px solid #d1d5db", borderRadius: "8px", boxSizing: "border-box" }} />
            </div>
          ))}
        </div>
        <button onClick={gpSaveAndReview} disabled={gpBusy}
          style={{ width: "100%", background: "#166534", color: "white", border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 700, cursor: "pointer", opacity: gpBusy ? 0.6 : 1 }}>
          {gpBusy ? "Saving…" : "Finish"}
        </button>
      </Page>
    );
  }

  if (gpScreen === "review") {
    const total = gpHoles.reduce((sum, h) => sum + (Number.isInteger(h.gross) ? h.gross : 0), 0);
    return (
      <Page wide>
        {backBtn(() => setGpScreen("score"), "Back to entry")}
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "16px" }}>Review Your Scorecard</div>
        {gpError && <div style={{ color: "#b91c1c", fontSize: "13px", marginBottom: "12px" }}>{gpError}</div>}
        <table style={{ width: "100%", fontSize: "13px", marginBottom: "16px", borderCollapse: "collapse" }}>
          <thead><tr style={{ textAlign: "left", color: "#6b7280" }}><th>Hole</th><th>Par</th><th>Gross</th></tr></thead>
          <tbody>
            {gpHoles.map(h => (
              <tr key={h.hole} style={{ borderTop: "1px solid #f3f4f6" }}><td>{h.hole}</td><td>{h.par}</td><td>{h.gross ?? "—"}</td></tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontWeight: 700, fontSize: "15px", marginBottom: "16px" }}>Total gross: {total}</div>
        <button onClick={gpSubmit} disabled={gpBusy}
          style={{ width: "100%", background: "#166534", color: "white", border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 700, cursor: "pointer", opacity: gpBusy ? 0.6 : 1 }}>
          {gpBusy ? "Submitting…" : "Submit General Play Score"}
        </button>
      </Page>
    );
  }

  if (gpScreen === "done") {
    return (
      <Page>
        <div style={{ fontSize: "40px", textAlign: "center", marginBottom: "8px" }}>✓</div>
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "10px", textAlign: "center" }}>Scores Saved</div>
        <div style={{ fontSize: "14px", color: "#374151", textAlign: "center", marginBottom: "20px" }}>Your General Play round has been recorded.</div>
        <button onClick={resetToStart}
          style={{ width: "100%", background: "#166534", color: "white", border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}>
          Done
        </button>
      </Page>
    );
  }

  // Screen 1: home. If there's no published competition and General Play is
  // available, the useEffect above already redirected us to gpScreen ===
  // "search" (handled earlier) before we ever reach here, so this point is
  // only reached when a competition exists, or when genuinely nothing is
  // set up for the kiosk today.
  if (!hasActiveComp && !gpInfo?.enabled) {
    // Genuinely nothing set up today -- say so plainly rather than showing
    // a dead-end surname box.
    return (
      <Page>
        <div style={{ fontSize: "40px", textAlign: "center", marginBottom: "8px" }}>⛳</div>
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "4px", textAlign: "center" }}>Golf Society Manager</div>
        <div style={{ fontSize: "13px", color: "#6b7280", textAlign: "center" }}>Nothing is set up for the kiosk today — please see your organiser.</div>
      </Page>
    );
  }

  return (
    <Page>
      <div style={{ fontSize: "40px", textAlign: "center", marginBottom: "8px" }}>⛳</div>
      <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "4px", textAlign: "center" }}>Golf Society Manager</div>
      <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "20px", textAlign: "center" }}>Type your surname to get started</div>
      <input
        type="text" value={surname} onChange={e => search(e.target.value)} placeholder="Surname"
        autoFocus
        style={{ width: "100%", border: "1.5px solid #d1d5db", borderRadius: "10px", padding: "13px 14px", fontSize: "16px", boxSizing: "border-box", marginBottom: "12px" }}
      />
      {searching && <div style={{ color: "#9ca3af", fontSize: "12px" }}>Searching…</div>}
      {matches.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {matches.map(m => (
            <button key={`${m.source}-${m.id}`} onClick={() => loadEvents(m)}
              style={{ textAlign: "left", background: "#f0fdf4", border: "1px solid #d1fae5", borderRadius: "8px", padding: "12px 14px", fontSize: "14px", fontWeight: 600, color: "#14532d", cursor: "pointer" }}>
              {m.name}
              {m.source === "list" && <span style={{ fontWeight: 400, color: "#6b7280" }}> — new to roster</span>}
            </button>
          ))}
        </div>
      )}
      {surname.trim().length >= 2 && !searching && matches.length === 0 && (
        <div style={{ color: "#9ca3af", fontSize: "12px" }}>No match found — check the spelling, or ask your organiser to add you.</div>
      )}
      {gpInfo?.enabled && (
        <button onClick={gpOpenSearch}
          style={{ width: "100%", marginTop: "16px", background: "none", border: "none", color: "#1d4ed8", fontSize: "14px", fontWeight: 700, cursor: "pointer", padding: "8px" }}>
          General Play →
        </button>
      )}
    </Page>
  );
}
